import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { api } from "../lib/api";
import { logger } from "../lib/logger";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Token is set as httpOnly cookie by backend; localStorage is a Bearer fallback
    // for environments where third-party cookies are blocked (CORS/iframe).
    if (data.access_token) localStorage.setItem("hm_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) localStorage.setItem("hm_token", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    // Backend clears the httpOnly cookie; a network error here is expected
    // (e.g. offline / already-expired session) and we still complete client-side logout.
    try { await api.post("/auth/logout"); } catch (e) {
      logger.warn("Logout API call failed (clearing client state anyway)", e);
      Sentry.captureException(e);
    }
    localStorage.removeItem("hm_token");
    setUser(null);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, setUser }),
    [user, loading, refresh],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
